import React, { useEffect, useId, useState } from 'react';
import './index.scss';

/**
 * 半圆仪表盘组件 - 支持动态数值与过渡动画
 * @param {number} value - 当前值 0-100
 * @param {number} size - 组件宽度/高度（保持比例）
 * @param {string} trackColor - 背景弧颜色
 * @param {string} fillColor - 进度弧颜色
 * @param {number} strokeWidth - 弧线粗细
 * @param {number} duration - 过渡动画时长（ms）
 * @param {boolean} showLabel - 是否在中心显示数值
 * @param {string} labelSuffix - 数值后缀（如 '%'）
 */
const CircularViewGauge = ({
  value = 0,
  size = 68,
  trackColor = '#E3E3E3',
  strokeWidth = 7,
  duration = 800,
  showLabel = true,
  labelSuffix = '',
  labelSize = 11,
  isAnalysising = false,
  isLoading = false,
}) => {

  const [displayValue, setDisplayValue] = useState(value);

  const clipId = useId().replace(/:/g, '-');
  const gradientId = `${clipId}-analysing`;

  const fillColor = isLoading ? '#ccc' : value <= 33 ? '#d33431' : value <= 66 ? '#f19310' : '#27ae60';

  // 数值变化时触发过渡动画
  useEffect(() => {
    const clamped = Math.min(100, Math.max(0, Number(value)));
    setDisplayValue(clamped);
  }, [value]);

  // viewBox 比例与原始设计一致
  const viewBoxWidth = 64;
  const viewBoxHeight = 38;
  const centerX = 32;
  // const radius = 20;

  // 半圆弧 path：从 (12, 28) 到 (52, 28)，圆心 (32, 28)
  const arcPath =
    'M12 28 C12 22.6957 14.1071 17.6086 17.8579 13.8579 C21.6086 10.1071 26.6957 8 32 8 C37.3043 8 42.3914 10.1071 46.1421 13.8579 C49.8929 17.6086 52 22.6957 52 28';

  // 用 pathLength=100 归一化，stroke-dashoffset = 100 - value 表示显示的比例
  const dashOffset = 100 - displayValue;

  return (
    <svg
      width={size}
      height={size * (viewBoxHeight / viewBoxWidth)}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`circular-view-gauge${isAnalysising ? ' circular-view-gauge--analysing' : ''}`}
    >
      <g clipPath={`url(#${clipId})`}>
        {isAnalysising || isLoading ? (
          // 分析中：整条弧使用浅色渐变，并做透明度闪烁（呼吸）动效
          <>
            <path
              className="circular-view-gauge__analysing-arc"
              d={arcPath}
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              fill="none"
              pathLength={100}
            />
            {showLabel && (
              <text
                x={centerX}
                y={29}
                textAnchor="middle"
                className="circular-view-gauge__label circular-view-gauge__label--analysing"
                style={{ fontSize: labelSize }}
              >
                --
              </text>
            )}
          </>
        ) : (
          <>
            {/* 背景弧 */}
            <path
              d={arcPath}
              stroke={trackColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              fill="none"
              pathLength={100}
            />
            {/* 进度弧 - 通过 dashoffset 做过渡动画 */}
            <path
              d={arcPath}
              stroke={fillColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              fill="none"
              pathLength={100}
              strokeDasharray={100}
              strokeDashoffset={dashOffset}
              style={{
                transition: `stroke-dashoffset ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              }}
            />
            {/* 中心数值 */}
            {showLabel && (
              <text
                x={centerX}
                y={29}
                textAnchor="middle"
                className="circular-view-gauge__label"
                style={{
                  transition: `opacity ${duration}ms ease-out`,
                  fontSize: labelSize,
                }}
              >
                {Math.round(displayValue)}
                {labelSuffix}
              </text>
            )}
          </>
        )}
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width={viewBoxWidth} height={viewBoxHeight} fill="white" />
        </clipPath>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#f6d6d6" />
          <stop offset="50%" stopColor="#fce9cf" />
          <stop offset="100%" stopColor="#d4efdf" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default CircularViewGauge;
